// AIChat.js — Chat com a Layla (assistente da Insigne Acadêmica).
// Portado da landing page (academica-insigne-landing-page) para o editora-insigne.
// Usa o MESMO backend: /api/landing/chat, /chat/limit, /chat/redirect.
// A chave de IA vive no backend; aqui só passamos as mensagens da sessão.
import { useState, useRef, useEffect } from "react";
import Icon from "../../assets/aichat/icon.png";

const API_URL = process.env.REACT_APP_API; // ex.: https://insigneacademica.com.br/api/
const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=5584992293892";

// Cliente HTTP mínimo (espelha api/client.ts da landing). `path` já sem o /api/.
const api = async (path, opts = {}) => {
  const { body, headers, ...rest } = opts;
  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const chatService = {
  send: (messages, sessionId) => api("landing/chat", { method: "POST", body: { messages, sessionId } }),
  limit: () => api("landing/chat/limit"),
  redirect: (trigger) => api("landing/chat/redirect", { method: "POST", body: { trigger } }),
};

const SESSION_KEY = "insigne_chat_session";
const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const getTime = () => new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const WELCOME_MESSAGE = {
  id: 0,
  role: "assistant",
  content:
    "Oi! Eu sou a Layla, assistente virtual da Insigne Acadêmica. Posso te ajudar com publicação de livro, revisão, orientação acadêmica, CIDA, Revista Insigne ou outro serviço editorial. Me conta: o que você precisa hoje? 😊",
  time: getTime(),
};

// Frases de intenção explícita de contato humano → encaminha ao WhatsApp antes da IA
// (assim o window.open não é bloqueado pelo browser por não ser gesto do usuário).
const hasContactIntent = (text) => {
  const keywords = [
    "falar com a equipe", "falar com alguém", "falar com uma pessoa",
    "falar com vocês", "falar com atendente", "falar com humano",
    "entrar em contato com vocês", "me ajudar pessoalmente",
    "chamar no whatsapp", "seu whatsapp", "qual o whatsapp", "me passa o whatsapp",
    "quero contratar", "quero assinar", "quero me inscrever",
    "quero ser insigne", "quero comprar", "quero agendar", "marcar reunião",
  ];
  const lower = text.toLowerCase();
  return keywords.some((k) => lower.includes(k));
};

function AIChat() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [limitMax, setLimitMax] = useState(10);
  const [blocked, setBlocked] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatService
      .limit()
      .then((l) => {
        setRemaining(l.remaining);
        setLimitMax(l.max);
        if (l.remaining === 0) setBlocked(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const redirectToWhatsApp = (reason) => {
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
    setMessages((prev) => [...prev, { id: Date.now(), role: "assistant", content: reason, time: getTime() }]);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || blocked) return;

    const userMsg = { id: Date.now(), role: "user", content: text, time: getTime() };

    if (hasContactIntent(text)) {
      window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
      chatService.redirect(`palavra-chave: ${text.slice(0, 80)}`).catch(() => {});
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: Date.now() + 1,
          role: "assistant",
          content: "Vou te encaminhar para nossa equipe no WhatsApp agora. Em instantes alguém estará em contato com você. 😊",
          time: getTime(),
        },
      ]);
      setInput("");
      return;
    }

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    const apiMessages = updatedMessages.filter((m) => m.id !== 0).map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await chatService.send(apiMessages, getSessionId());
      setMessages((prev) => [...prev, { id: Date.now() + 1, role: "assistant", content: res.reply, time: getTime() }]);
      setRemaining(res.usage.remaining);
      if (res.usage.remaining === 0) {
        setBlocked(true);
        setTimeout(() => {
          redirectToWhatsApp(
            "Essa foi nossa última mensagem por aqui hoje. Vou te encaminhar para nossa equipe no WhatsApp, que pode continuar te atendendo com mais profundidade. 💛"
          );
        }, 800);
      }
    } catch (err) {
      if (err.status === 429) {
        setBlocked(true);
        redirectToWhatsApp(
          err.data?.message || "Você atingiu o limite de mensagens por aqui. Vou te encaminhar para nossa equipe no WhatsApp."
        );
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: "assistant",
            content: "⚠️ Tive um problema para te responder agora. Tenta de novo em um instante, ou fala direto com a equipe pelo WhatsApp.",
            time: getTime(),
          },
        ]);
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  const remainingLabel =
    remaining !== null && remaining <= 3 && !blocked
      ? `${remaining} mensagem${remaining === 1 ? "" : "s"} restante${remaining === 1 ? "" : "s"}`
      : null;

  return (
    <div
      className="w-full mx-auto rounded-2xl overflow-hidden shadow-2xl"
      style={{ maxWidth: "900px", border: "1px solid rgba(255,134,17,0.3)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#0d1226]" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <img src={Icon} alt="Layla" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
        <div className="flex-1">
          <p className="text-white font-semibold text-sm leading-tight">Layla</p>
          <p className="text-green-400 text-xs">● Online · Insigne Acadêmica</p>
        </div>
        {remainingLabel && (
          <span
            className="text-amber-400 text-[10px] font-medium px-2 py-1 rounded-full"
            style={{ background: "rgba(255,134,17,0.1)", border: "1px solid rgba(255,134,17,0.3)" }}
          >
            {remainingLabel}
          </span>
        )}
      </div>

      {/* Área de mensagens */}
      <div
        ref={containerRef}
        className="flex flex-col gap-3 p-4 overflow-y-auto"
        style={{
          height: "clamp(320px, 50vw, 480px)",
          background: "linear-gradient(to bottom, #060d1f, #0d1226)",
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      >
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={
                "relative px-4 py-2 rounded-2xl text-sm leading-relaxed shadow-md " +
                (msg.role === "user" ? "bg-[#FF8611] text-white rounded-br-sm" : "bg-[#16163f] text-gray-100 rounded-bl-sm")
              }
              style={{
                maxWidth: "min(78%, 600px)",
                border: msg.role === "assistant" ? "1px solid rgba(255,255,255,0.07)" : "none",
              }}
            >
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
              <span className={"block text-right text-[10px] mt-1 select-none " + (msg.role === "user" ? "text-white/60" : "text-gray-500")}>
                {msg.time}
              </span>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#16163f] px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center" style={{ border: "1px solid rgba(255,255,255,0.07)" }}>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}
      </div>

      {/* Campo de input */}
      <div className="flex items-center gap-2 px-3 py-3 bg-[#0d1226]" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={blocked ? `Limite de ${limitMax} mensagens atingido. Fale com nossa equipe no WhatsApp` : "Digite sua mensagem..."}
          disabled={loading || blocked}
          className="flex-1 bg-[#16163f] text-white placeholder-gray-500 text-sm px-4 py-3 rounded-full outline-none focus:ring-2 focus:ring-[#FF8611]/50 disabled:opacity-50 transition-all"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
        />
        {blocked ? (
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Continuar no WhatsApp"
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "#25D366", color: "white", textDecoration: "none" }}
          >
            <i className="bi bi-whatsapp text-lg" />
          </a>
        ) : (
          <button
            type="button"
            onClick={() => { if (!loading && input.trim()) sendMessage(); }}
            disabled={loading || !input.trim()}
            aria-label="Enviar mensagem"
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#FF8611", color: "white" }}
          >
            <i className="bi bi-send-fill text-base" />
          </button>
        )}
      </div>
    </div>
  );
}

export default AIChat;
